<template>
  <header
    class="layout-header tw:relative tw:sticky tw:top-0 tw:z-30 tw:w-full tw:backdrop-blur-md tw:border-b tw:flex tw:flex-col tw:xl:h-16 tw:xl:flex-row tw:xl:items-center tw:justify-between tw:py-2 tw:xl:py-0 tw:text-slate-100 tw:transition-[background-color,border-color,box-shadow] tw:duration-200"
    :class="isScrolled
      ? 'tw:bg-slate-950/95 tw:border-slate-700/80 tw:shadow-[0_10px_30px_-18px_rgba(2,6,23,0.9)]'
      : 'tw:bg-slate-950/80 tw:border-slate-800/60'"
  >
    <div class="tw:flex tw:w-full tw:items-center tw:justify-start tw:xl:justify-between tw:gap-3 tw:px-4 tw:xl:px-4 tw:2xl:px-6">
      <!-- Left: Burger Toggle and Title -->
      <div class="tw:flex tw:min-w-0 tw:items-center tw:gap-3 tw:md:hidden tw:xl:flex tw:xl:flex-1 tw:xl:pr-2">
        <!-- Mobile Burger Menu -->
        <button
          type="button"
          @click="$emit('toggle-mobile')"
          class="tw:md:hidden tw:w-10 tw:h-10 tw:rounded-xl tw:flex tw:items-center tw:justify-center tw:text-slate-100 tw:bg-slate-900 tw:hover:text-slate-100 tw:hover:bg-slate-800 tw:border tw:border-slate-700/80 tw:hover:border-slate-600 tw:shadow-premium tw:focus-visible:ring-2 tw:focus-visible:ring-indigo-500/70 tw:focus-visible:ring-offset-2 tw:focus-visible:ring-offset-slate-950 tw:transition-colors tw:shrink-0 tw:cursor-pointer"
          aria-label="Open navigation menu"
          title="Open navigation menu"
        >
          <MenuIcon class="tw:w-5 tw:h-5 tw:stroke-[2.5]" />
        </button>

        <!-- Page Breadcrumb / Title -->
        <div class="breadcrumb-track tw:hidden tw:xl:flex tw:flex-1 tw:flex-nowrap tw:items-center tw:gap-1.5 tw:text-sm tw:font-medium tw:ml-2 tw:min-w-0 tw:max-w-[min(50vw,42rem)] tw:overflow-x-auto tw:scrollbar-none tw:whitespace-nowrap">
          <template v-for="(item, idx) in breadcrumbItems" :key="idx">
            <span v-if="idx > 0" class="breadcrumb-separator tw:text-slate-700 tw:shrink-0">/</span>
            <router-link
              v-if="item.to"
              :to="item.to"
              :title="item.label"
              :class="[
                idx === breadcrumbItems.length - 1
                  ? 'breadcrumb-segment breadcrumb-segment-current tw:text-slate-200 tw:font-semibold tw:capitalize tw:hover:text-slate-100 tw:transition-colors'
                  : 'breadcrumb-segment tw:text-slate-500 tw:hover:text-slate-300 tw:transition-colors'
              ]"
            >
              {{ item.label }}
            </router-link>
            <span
              v-else
              :title="item.label"
              :class="[
                idx === breadcrumbItems.length - 1
                  ? 'breadcrumb-segment breadcrumb-segment-current tw:text-slate-200 tw:font-semibold tw:capitalize'
                  : 'breadcrumb-segment tw:text-slate-500'
              ]"
            >
              {{ item.label }}
            </span>
          </template>
        </div>
      </div>

      <!-- Right: Search, Notifications, Referral, Help, User Profile -->
      <div class="header-actions tw:ml-auto tw:flex tw:shrink-0 tw:items-center tw:gap-1.5 tw:md:gap-2 tw:flex-wrap">
      <!-- Mobile Search Toggle -->
      <div class="tw:relative mobile-search-toggle" v-click-outside="closeMobileSearch">
        <button
          @click="toggleMobileSearch"
          class="tw:w-9 tw:h-9 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-900 tw:border tw:border-slate-800/40 tw:transition-colors tw:shrink-0 tw:cursor-pointer"
          aria-label="Search pages"
        >
          <Search class="tw:w-4.5 tw:h-4.5" />
        </button>

        <Transition name="dropdown">
          <div
            v-if="showMobileSearch"
            class="tw:fixed tw:top-[4.5rem] tw:left-2 tw:right-2 tw:rounded-xl tw:bg-slate-900 tw:border tw:border-slate-800/80 tw:shadow-premium tw:p-2 tw:z-50"
          >
            <div class="tw:relative">
              <Search class="tw:absolute tw:left-3 tw:top-2.5 tw:w-4 tw:h-4 tw:text-slate-500" />
              <input
                ref="mobileSearchInput"
                v-model="searchQuery"
                @keydown="handleSearchKeyDown"
                type="text"
                placeholder="Search pages..."
                class="tw:w-full tw:h-9 tw:bg-slate-950/70 tw:border tw:border-slate-800/60 tw:hover:border-slate-700/80 tw:focus:border-indigo-500 tw:rounded-lg tw:pl-9 tw:pr-3 tw:text-sm tw:text-slate-200 tw:placeholder-slate-500 tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-indigo-500 tw:transition-all"
              />
            </div>

            <div
              v-if="filteredRoutes.length > 0"
              class="tw:mt-2 tw:space-y-0.5 tw:max-h-60 tw:overflow-y-auto"
            >
              <button
                v-for="(route, idx) in filteredRoutes"
                :key="route.name"
                @click="navigateSearch(route)"
                @mouseenter="setActiveSearchIndex(idx)"
                class="tw:w-full tw:text-left tw:px-2.5 tw:py-1.5 tw:text-sm tw:rounded-lg tw:text-slate-300 tw:font-medium tw:transition-colors tw:cursor-pointer tw:flex tw:items-center tw:gap-2.5 tw:group"
                :class="idx === activeSearchIndex ? 'tw:bg-slate-800/60 tw:text-indigo-300' : 'tw:hover:bg-slate-800/60 tw:hover:text-indigo-300'"
              >
                <component :is="getRouteIcon(route.meta)" class="tw:w-4 tw:h-4 tw:text-slate-500 tw:group-hover:text-indigo-400 tw:transition-colors tw:shrink-0" />
                <span class="tw:truncate">{{ route.getRoutePathString() }}</span>
                <span v-if="route.matchedAlias" class="tw:ml-auto tw:text-2xs tw:text-slate-500 tw:shrink-0 tw:italic">
                  was &ldquo;{{ route.matchedAlias }}&rdquo;
                </span>
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Search Input (Interactive Page-Jumping Command Palette) -->
      <div class="header-search-full tw:hidden tw:relative tw:w-50 tw:sm:w-52 tw:md:w-54 tw:lg:w-56 tw:xl:w-60 tw:2xl:w-72" data-tour="header-search" v-click-outside="closeSearch">
        <Search class="tw:absolute tw:left-3 tw:top-2.5 tw:w-4 tw:h-4 tw:text-slate-500" />
        <input
          ref="searchInput"
          v-model="searchQuery"
          @keydown="handleSearchKeyDown"
          type="text"
          placeholder="Search pages..."
          class="tw:w-full tw:h-9 tw:bg-slate-900 tw:border tw:border-slate-800/60 tw:hover:border-slate-700/80 tw:focus:border-indigo-500 tw:rounded-lg tw:pl-9 tw:pr-10 tw:text-sm tw:text-slate-200 tw:placeholder-slate-500 tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-indigo-500 tw:transition-all"
        />
        <div
          class="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:flex tw:h-6 tw:min-w-11 tw:items-center tw:justify-center tw:px-1.5 tw:rounded tw:border tw:border-slate-800 tw:bg-slate-955 tw:text-2xs tw:font-mono tw:leading-none tw:text-slate-500 tw:select-none"
          :class="searchShortcutModifier === 'Ctrl' ? 'tw:gap-1.5' : 'tw:gap-0.5'"
        >
          <span>{{ searchShortcutModifier }}</span><span>K</span>
        </div>

        <!-- Search Results Dropdown -->
        <Transition name="dropdown">
          <div
            v-if="filteredRoutes.length > 0"
            class="tw:absolute tw:top-full tw:right-0 tw:max-xl:left-0 tw:max-xl:right-auto tw:w-[350px] tw:lg:w-[450px] tw:max-xl:max-w-[calc(100vw-2rem)] tw:mt-2 tw:rounded-xl tw:bg-slate-900 tw:border tw:border-slate-800/80 tw:shadow-premium tw:p-1.5 tw:z-50 tw:text-left"
          >
            <div class="tw:px-2.5 tw:py-1 tw:text-2xs tw:font-bold tw:text-slate-500 tw:uppercase tw:tracking-wider tw:mb-1 tw:border-b tw:border-slate-800/40">
              Jump to Page
            </div>
            <div class="tw:space-y-0.5 tw:max-h-60 tw:overflow-y-auto">
              <button
                v-for="(route, idx) in filteredRoutes"
                :key="route.name"
                @click="navigateSearch(route)"
                @mouseenter="setActiveSearchIndex(idx)"
                class="tw:w-full tw:text-left tw:px-2.5 tw:py-1.5 tw:text-sm tw:rounded-lg tw:text-slate-300 tw:font-medium tw:transition-colors tw:cursor-pointer tw:flex tw:items-center tw:gap-2.5 tw:group"
                :class="idx === activeSearchIndex ? 'tw:bg-slate-800/60 tw:text-indigo-300' : 'tw:hover:bg-slate-800/60 tw:hover:text-indigo-300'"
              >
                <component :is="getRouteIcon(route.meta)" class="tw:w-4 tw:h-4 tw:text-slate-500 tw:group-hover:text-indigo-400 tw:transition-colors tw:shrink-0" />
                <span>{{ route.getRoutePathString() }}</span>
                <span v-if="route.matchedAlias" class="tw:ml-auto tw:text-2xs tw:text-slate-500 tw:shrink-0 tw:italic">
                  was &ldquo;{{ route.matchedAlias }}&rdquo;
                </span>
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <div class="header-actions-trailing tw:flex tw:items-center tw:gap-1.5 tw:md:gap-2 tw:ml-auto">

      <!-- Notifications Dropdown Bell -->
      <notification-bell v-if="dashboardNotificationsEnabled" />

      <!-- Refer Customer Dropdown -->
      <div v-if="showReferralDropdown" class="tw:relative tw:group/tooltip" v-click-outside="closeReferral">
        <button
          @click="showReferral = !showReferral"
          class="tw:w-9 tw:h-9 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-[#FF5352] tw:hover:text-[#ff6e6d] tw:bg-[#FF5352]/10 tw:hover:bg-[#FF5352]/20 tw:border tw:border-[#FF5352]/30 tw:transition-colors tw:shrink-0 tw:cursor-pointer referral-btn"
        >
          <Gift class="tw:w-4.5 tw:h-4.5" />
        </button>
        <div 
          class="tw:absolute tw:top-full tw:mt-2 tw:left-1/2 tw:-translate-x-1/2 tw:px-2.5 tw:py-1 tw:bg-slate-900 tw:border tw:border-slate-800 tw:text-slate-200 tw:text-2xs tw:font-semibold tw:rounded-lg tw:opacity-0 tw:pointer-events-none tw:group-hover/tooltip:opacity-100 tw:group-hover/tooltip:translate-y-0 tw:translate-y-[-4px] tw:transition-all tw:duration-200 tw:shadow-premium tw:whitespace-nowrap tw:z-50"
          :class="{ '!tw:opacity-0': showReferral }"
        >
          Refer Customer
        </div>

        <!-- Referral dropdown card -->
        <Transition name="dropdown">
          <div
            v-if="showReferral"
            class="tw:absolute tw:right-0 tw:mt-2 tw:w-80 tw:sm:w-96 tw:rounded-xl tw:bg-slate-900 tw:border tw:border-slate-800/80 tw:shadow-premium tw:p-4 tw:z-50 tw:text-left tw:text-slate-200"
          >
            <div class="tw:font-bold tw:text-sm tw:text-slate-100">
              Refer Fareclock To Your Network.<span class="tw:text-[#FF5352]"> Get A Reward.</span>
            </div>
            <p class="tw:text-sm tw:text-slate-400 tw:mt-2 tw:leading-relaxed">
              If you know anyone who could use a hand with their team management, introduce us!
              Receive a <strong class="tw:text-slate-200">USD $100 gift card</strong> reward for every successful sign-up.
            </p>
            <div class="tw:flex tw:items-center tw:gap-3 tw:mt-4">
              <button
                @click="launchLearnMore"
                class="tw:flex-1 tw:py-2 tw:bg-indigo-600 tw:hover:bg-indigo-500 tw:font-bold tw:text-sm tw:text-white tw:rounded-xl tw:transition-colors tw:cursor-pointer tw:text-center"
              >
                Learn More
              </button>
              <button
                @click="launchQuestion"
                class="tw:flex-1 tw:py-2 tw:bg-[#FF5352] tw:hover:bg-[#ff6e6d] tw:font-bold tw:text-sm tw:text-white tw:rounded-xl tw:transition-colors tw:cursor-pointer tw:text-center"
              >
                I have a question
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Support chat: one click to a person, with a count of replies waiting to be read. -->
      <div v-if="supportChatAvailable" class="tw:relative tw:group/tooltip" data-tour="header-chat">
        <button
          @click="toggleSupportChat"
          :aria-label="supportUnreadCount > 0 ? `Chat with support (${supportUnreadCount} unread)` : 'Chat with support'"
          class="tw:relative tw:w-9 tw:h-9 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-900 tw:border tw:border-slate-800/40 tw:transition-colors tw:shrink-0 tw:cursor-pointer"
        >
          <MessageCircle class="tw:w-4.5 tw:h-4.5" />
          <span
            v-if="supportUnreadCount > 0"
            class="tw:absolute tw:-top-1 tw:-right-1 tw:min-w-4 tw:h-4 tw:px-1 tw:rounded-full tw:bg-rose-500 tw:text-white tw:text-2xs tw:font-semibold tw:leading-4 tw:text-center tw:ring-2 tw:ring-slate-950"
            aria-hidden="true"
          >{{ supportUnreadCount > 9 ? '9+' : supportUnreadCount }}</span>
        </button>
        <div class="tw:absolute tw:top-full tw:mt-2 tw:left-1/2 tw:-translate-x-1/2 tw:px-2.5 tw:py-1 tw:bg-slate-900 tw:border tw:border-slate-800 tw:text-slate-200 tw:text-2xs tw:font-semibold tw:rounded-lg tw:opacity-0 tw:pointer-events-none tw:group-hover/tooltip:opacity-100 tw:group-hover/tooltip:translate-y-0 tw:translate-y-[-4px] tw:transition-all tw:duration-200 tw:shadow-premium tw:whitespace-nowrap tw:z-50">
          Chat with support
        </div>
      </div>

      <!-- Help Dropdown -->
      <div class="tw:relative tw:group/tooltip" data-tour="header-help" v-click-outside="closeHelp">
        <button
          @click="showHelp = !showHelp"
          :aria-label="whatsNewUnreadCount > 0 ? 'Help & Support (new updates)' : 'Help & Support'"
          class="tw:relative tw:w-9 tw:h-9 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-900 tw:border tw:border-slate-800/40 tw:transition-colors tw:shrink-0 tw:cursor-pointer"
        >
          <HelpCircle class="tw:w-4.5 tw:h-4.5" />
          <span
            v-if="whatsNewUnreadCount > 0"
            class="tw:absolute tw:top-1.5 tw:right-1.5 tw:w-2 tw:h-2 tw:rounded-full tw:bg-indigo-500 tw:ring-2 tw:ring-slate-950"
            aria-hidden="true"
          />
        </button>
        <div 
          class="tw:absolute tw:top-full tw:mt-2 tw:left-1/2 tw:-translate-x-1/2 tw:px-2.5 tw:py-1 tw:bg-slate-900 tw:border tw:border-slate-800 tw:text-slate-200 tw:text-2xs tw:font-semibold tw:rounded-lg tw:opacity-0 tw:pointer-events-none tw:group-hover/tooltip:opacity-100 tw:group-hover/tooltip:translate-y-0 tw:translate-y-[-4px] tw:transition-all tw:duration-200 tw:shadow-premium tw:whitespace-nowrap tw:z-50"
          :class="{ '!tw:opacity-0': showHelp }"
        >
          Help & Support
        </div>

        <!-- Help Dropdown Menu -->
        <Transition name="dropdown">
          <div
            v-if="showHelp"
            class="tw:absolute tw:right-0 tw:mt-2 tw:w-48 tw:rounded-xl tw:bg-slate-900 tw:border tw:border-slate-800/80 tw:shadow-premium tw:py-1.5 tw:z-50 tw:text-left tw:text-slate-200"
          >
            <!--
              Ordered by cost to the person and to us, cheapest first: self-serve docs, the
              self-serve tour and What's New, then support chat (answered by AI first, so
              cheap to offer), then the one that books a support agent for 15 minutes. Feedback sits under a divider because it is the odd one out -- the
              person is giving us something rather than asking for help. Not alphabetical: at
              five items there is no scanning problem to solve, and sorting by first letter
              would put booking a human above reading the docs.
            -->
            <button
              v-if="supportChatAvailable"
              @click="launchHelp"
              class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-slate-800/50 tw:text-slate-350 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
            >
              <HelpCircle class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
              <span>Help articles</span>
            </button>
            <button
              @click="launchTour"
              class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-slate-800/50 tw:text-slate-350 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
            >
              <Compass class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
              <span>{{ tourMenuLabel }}</span>
            </button>
            <button
              @click="launchWhatsNew"
              class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-slate-800/50 tw:text-slate-350 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
            >
              <Sparkles class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
              <span>What's new</span>
              <span
                v-if="whatsNewUnreadCount > 0"
                class="tw:ml-auto tw:rounded-full tw:bg-indigo-600 tw:px-1.5 tw:text-2xs tw:font-semibold tw:text-white"
              >{{ whatsNewUnreadCount }}</span>
            </button>
            <button
              v-if="supportChatAvailable"
              @click="launchSupportChat"
              class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-slate-800/50 tw:text-slate-350 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
            >
              <MessageCircle class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
              <span>Chat with support</span>
              <span
                v-if="supportUnreadCount > 0"
                class="tw:ml-auto tw:rounded-full tw:bg-rose-500 tw:px-1.5 tw:text-2xs tw:font-semibold tw:text-white"
              >{{ supportUnreadCount }}</span>
            </button>
            <button
              @click="launchWalkthrough"
              class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-slate-800/50 tw:text-slate-350 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
            >
              <Video class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
              <span>Book a walkthrough</span>
            </button>

            <div class="tw:h-px tw:bg-slate-800/50 tw:my-1.5"></div>

            <button
              @click="launchFeedback"
              class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:hover:bg-slate-800/50 tw:text-slate-350 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
            >
              <MessageSquare class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
              <span>Feedback</span>
            </button>
          </div>
        </Transition>
      </div>

      <a
        :href="legacyConsoleRevertUrl"
        class="tw:inline-flex tw:items-center tw:rounded-full tw:border tw:px-2 tw:py-1 tw:text-2xs tw:font-semibold tw:transition-colors tw:whitespace-nowrap"
        :class="[
          isRevertingToLegacy ? 'tw:pointer-events-none tw:opacity-60' : '',
          isLightOrBalancedTheme
            ? 'tw:border-rose-600/55 tw:bg-rose-50 tw:text-rose-700 tw:hover:text-rose-800 tw:hover:bg-rose-100'
            : 'tw:border-rose-500/45 tw:bg-rose-500/10 tw:text-rose-300 tw:hover:text-rose-200 tw:hover:bg-rose-500/18'
        ]"
        data-tour="header-classic"
        title="Switch to Classic view"
        aria-label="Switch to Classic view"
        @click.prevent="onRevertToLegacyClick"
      >
        <span>Classic</span>
      </a>

      <!-- User Profile Avatar -->
      <div class="tw:relative tw:group/tooltip tw:flex tw:items-center tw:shrink-0" data-tour="header-profile" v-click-outside="closeUserMenu">
        <div
          @click="showUserMenu = !showUserMenu"
          class="tw:w-9 tw:h-9 tw:rounded-full tw:ring-2 tw:ring-indigo-500/25 tw:hover:ring-indigo-500/50 tw:cursor-pointer tw:overflow-hidden tw:transition-all tw:bg-slate-800 tw:flex tw:items-center tw:justify-center tw:font-bold tw:text-xs tw:text-indigo-300"
        >
          <img
            v-if="avatarUrl"
            :src="avatarUrl"
            alt="User avatar"
            class="tw:w-full tw:h-full tw:object-cover"
            referrerpolicy="no-referrer"
          />
          <span v-else>{{ avatarText }}</span>
        </div>
        <div 
          class="tw:absolute tw:top-full tw:mt-2 tw:right-0 tw:px-2.5 tw:py-1 tw:bg-slate-900 tw:border tw:border-slate-800 tw:text-slate-200 tw:text-2xs tw:font-semibold tw:rounded-lg tw:opacity-0 tw:pointer-events-none tw:group-hover/tooltip:opacity-100 tw:group-hover/tooltip:translate-y-0 tw:translate-y-[-4px] tw:transition-all tw:duration-200 tw:shadow-premium tw:whitespace-nowrap tw:z-50"
          :class="{ '!tw:opacity-0': showUserMenu }"
        >
          {{ userName }}
        </div>

        <!-- User Dropdown Menu -->
        <Transition name="dropdown">
          <div
            v-if="showUserMenu"
            class="tw:absolute tw:right-0 tw:top-full tw:mt-2 tw:w-56 tw:rounded-xl tw:bg-slate-900 tw:border tw:border-slate-800/80 tw:shadow-premium tw:py-2 tw:z-50 tw:text-left tw:text-slate-200"
          >
            <!-- User Header Info -->
            <div class="tw:px-4 tw:py-2.5 tw:border-b tw:border-slate-800/60 tw:mb-1.5 tw:flex tw:flex-col tw:min-w-0">
              <span class="tw:font-semibold tw:text-sm tw:text-slate-100 tw:truncate">{{ userName }}</span>
              <span class="tw:text-sm tw:text-slate-400 tw:truncate tw:mt-0.5">{{ emailAddress }}</span>
            </div>

            <!-- Menu items -->
            <div class="tw:px-1.5 tw:space-y-0.5">
              <a
                :href="`${loginServer}/manage`"
                target="_blank"
                class="tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-slate-800/50 tw:text-slate-300 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
                @click="closeUserMenu"
              >
                <User class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>My Profile</span>
              </a>
              
              <a
                :href="`${loginServer}/change-password`"
                target="_blank"
                class="tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-slate-800/50 tw:text-slate-300 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer"
                @click="closeUserMenu"
              >
                <KeyRound class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>Change Password</span>
              </a>

              <button
                class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-slate-800/50 tw:text-slate-300 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer tw:text-left"
                @click="onLocalSettingsClick"
              >
                <Settings class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>Local Settings</span>
              </button>

              <button
                class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-slate-800/50 tw:text-slate-300 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer tw:text-left"
                @click="onSwitchOrgClick"
              >
                <Building class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>Switch Organization</span>
              </button>

              <button
                v-if="isSudoAccessToAllOrgs"
                class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-slate-800/50 tw:text-slate-300 tw:hover:text-slate-100 tw:transition-colors tw:cursor-pointer tw:text-left"
                @click="goToSudo"
              >
                <Crown class="tw:text-slate-500 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>Sudo</span>
              </button>

              <button
                class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-rose-500/10 tw:text-rose-400 tw:hover:text-rose-300 tw:transition-colors tw:cursor-pointer tw:text-left"
                :class="isRevertingToLegacy ? 'tw:pointer-events-none tw:opacity-60' : ''"
                @click="onRevertToLegacyClick"
              >
                <AlertTriangle class="tw:text-rose-500/80 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>Switch to Classic view</span>
              </button>

              <div class="tw:h-px tw:bg-slate-800/60 tw:my-1"></div>

              <!-- Theme picker: inline so the current theme is visible and switching is one click -->
              <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-3 tw:py-1.5 tw:select-none">
                <span class="tw:text-sm tw:font-medium tw:text-slate-300">Theme</span>
                <div class="tw:flex tw:items-center tw:gap-0.5 tw:p-0.5 tw:rounded-lg tw:border tw:border-slate-800/60" role="group" aria-label="Theme">
                  <button
                    v-for="option in themeOptions"
                    :key="option.value"
                    type="button"
                    @click="applyTheme(option.value)"
                    :title="option.label"
                    :aria-label="option.label"
                    :aria-pressed="theme === option.value"
                    class="tw:w-6 tw:h-6 tw:rounded-md tw:flex tw:items-center tw:justify-center tw:transition-colors tw:cursor-pointer"
                    :class="theme === option.value ? 'tw:bg-indigo-500/20 tw:ring-1 tw:ring-indigo-500/50' : 'tw:hover:bg-slate-800/50'"
                  >
                    <component :is="option.icon" class="tw:w-3.5 tw:h-3.5" :class="option.iconClass" />
                  </button>
                </div>
              </div>

              <div class="tw:h-px tw:bg-slate-800/60 tw:my-1"></div>

              <button
                class="tw:w-full tw:flex tw:items-center tw:gap-2.5 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:rounded-lg tw:hover:bg-rose-500/10 tw:text-rose-400 tw:hover:text-rose-300 tw:transition-colors tw:cursor-pointer tw:text-left"
                @click="onLogoutClick"
              >
                <LogOut class="tw:text-rose-500/80 tw:w-4 tw:h-4 tw:shrink-0" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </Transition>
      </div>
      </div>
      </div>
    </div>

    <!-- Mobile and tablet breadcrumb line -->
    <div class="tw:w-full tw:xl:hidden tw:mt-1 tw:pt-1 tw:px-4">
      <div class="mobile-breadcrumb-track tw:flex tw:items-center tw:min-w-0 tw:gap-1 tw:text-[13px] tw:font-medium tw:whitespace-nowrap">
        <template v-for="(item, idx) in breadcrumbItems" :key="`mobile-${idx}`">
          <span v-if="idx > 0" class="mobile-breadcrumb-separator tw:text-slate-700 tw:shrink-0">/</span>
          <router-link
            v-if="item.to"
            :to="item.to"
            :title="item.label"
            :class="[
              'mobile-breadcrumb-segment',
              idx === 0 ? 'mobile-breadcrumb-org tw:text-slate-250' : 'mobile-breadcrumb-tail tw:text-slate-500',
              idx === breadcrumbItems.length - 1 ? 'tw:text-slate-200 tw:font-semibold tw:capitalize' : 'tw:hover:text-slate-300 tw:transition-colors'
            ]"
          >
            {{ item.label }}
          </router-link>
          <span
            v-else
            :title="item.label"
            :class="[
              'mobile-breadcrumb-segment',
              idx === 0 ? 'mobile-breadcrumb-org tw:text-slate-250' : 'mobile-breadcrumb-tail tw:text-slate-500',
              idx === breadcrumbItems.length - 1 ? 'tw:text-slate-200 tw:font-semibold tw:capitalize' : ''
            ]"
          >
            {{ item.label }}
          </span>
        </template>
      </div>
    </div>
  </header>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { mapState, mapGetters, mapActions } from 'vuex'
import { useDialog } from 'primevue/usedialog'
import { openIntercom, openSupportChat, toggleSupportChat as toggleIntercomSupportChat } from '@/utils/intercom'
import { useIntercomMessenger } from '@/composables/useIntercomMessenger'
import FeedbackModal from './FeedbackModal.vue'
import { useBootstrapVueModal } from '@/composables/useBootstrapVueModal'
import { promptLocalSettings } from '@/components/local-settings-dialog'
import { promptClassicConsoleFeedback } from '@/components/classic-console-feedback-dialog'
import { promptScheduleWalkthrough } from '@/components/schedule-walkthrough-dialog'
import { promptConsoleTourInvitation } from '@/components/tour/tour-invitation-dialog'
import { TOUR_MENU_LABEL } from '@/components/tour/console-tour-steps'
import { useConsoleTourRunner } from '@/composables/useConsoleTour'
import { useConsoleTourStore, TOUR_MIN_VIEWPORT_WIDTH } from '@/stores/consoleTour'
import { useWhatsNewStore } from '@/stores/whatsNew'
import { useWhatsNew } from '@/components/whats-new/useWhatsNew'
import { hasAccessToRouteName } from '@/router/RouterAccessGuard'
import { getRoutePathString, getSearchableRouteRecords } from '@/router/routeCatalog'
import { isNavIconName, resolveNavIcon } from '@/components/Sidebar/navIcons'
import { matchSearchableRoutes } from '@/utils/routeSearch'
import NotificationBell from './NotificationBell.vue'
import store from '@/store'
import constants from '@/constants'
import {
  Menu as MenuIcon,
  Search,
  Sun,
  Moon,
  Monitor,
  Contrast,
  SunDim,
  Gift,
  HelpCircle,
  MessageCircle,
  MessageSquare,
  User,
  KeyRound,
  Settings,
  Crown,
  LogOut,
  Building,
  LayoutDashboard,
  Clock,
  Calendar,
  Cpu,
  Users,
  Clipboard,
  Coins,
  CreditCard,
  BarChart3,
  AlertTriangle,
  Home,
  Briefcase,
  Shield,
  FileText,
  Bell,
  Wand2,
  Network,
  Video,
  Compass,
  Sparkles
} from '@lucide/vue'

const loginServer = constants().loginServer
const LEGACY_CONSOLE_PREFERENCE_KEY = 'LEGACY_CONSOLE_FROM_NEW_2'
const LEGACY_CONSOLE_REVERT_URL = 'https://legacy.console.fareclock.com?to-legacy'

const sidebarLabels = {
  overview: 'Overview',
  attendance: 'Attendance',
  schedule: 'Schedule',
  devices: 'Devices',
  users: 'Users',
  timeoff: 'Time Off',
  forms: 'Forms',
  costing: 'Job Costing',
  payroll: 'Payroll',
  systemops: 'System Ops',
  reports: 'Reports Center',
  byxbi: 'Byxbi',
  system: 'Settings'
}

export default {
  name: 'AppHeader',
  components: {
    NotificationBell,
    MenuIcon,
    Search,
    Gift,
    HelpCircle,
    MessageCircle,
    MessageSquare,
    Video,
    Compass,
    Sparkles,
    User,
    KeyRound,
    Settings,
    Crown,
    AlertTriangle,
    LogOut,
    Building
  },
  props: {
    isPinned: {
      type: Boolean,
      required: true
    },
    activePage: {
      type: String,
      required: true
    },
    isScrolled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-mobile', 'navigate', 'customize-sidebar'],
  setup (props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const dialog = useDialog()
    const { showModal } = useBootstrapVueModal()
    const consoleTourStore = useConsoleTourStore()
    const { run: runConsoleTour } = useConsoleTourRunner(dialog)
    const whatsNewStore = useWhatsNewStore()
    const { unread: whatsNewUnread } = useWhatsNew()
    const whatsNewUnreadCount = computed(() => whatsNewUnread.value.length)
    const { available: supportChatAvailable, unreadCount: supportUnreadCount } = useIntercomMessenger()
    const legacyConsoleRevertUrl = LEGACY_CONSOLE_REVERT_URL
    const isRevertingToLegacy = ref(false)

    const onRevertToLegacyClick = async () => {
      if (isRevertingToLegacy.value) {
        return
      }

      const shouldProceed = await promptClassicConsoleFeedback(dialog, {
        onCustomizeSidebar: () => emit('customize-sidebar')
      })
      if (!shouldProceed) {
        return
      }

      isRevertingToLegacy.value = true
      // Store today's local, timezone-naive date so the preference expires at
      // local midnight (checked in index.html).
      const now = new Date()
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      window.localStorage.setItem(LEGACY_CONSOLE_PREFERENCE_KEY, today)
      window.location.assign(legacyConsoleRevertUrl)
    }

    // Active page display name
    const activePageDisplayName = computed(() => {
      return route.meta?.label || route.meta?.title || route.name || ''
    })

    // Search query routing
    const searchQuery = ref('')
    const searchInput = ref(null)
    const mobileSearchInput = ref(null)
    const showMobileSearch = ref(false)
    const activeSearchIndex = ref(-1)
    const searchShortcutModifier = navigator.userAgent.includes('Mac') ? '⌘' : 'Ctrl'

    const searchableRoutes = computed(() => {
      const routes = getSearchableRouteRecords(router)
        .filter(r => hasAccessToRouteName(router, store, r.name))
        .map(r => ({
          name: r.name,
          meta: r.meta,
          isCustom: false,
          navLabel: r.meta.navLabel,
          searchAliases: r.meta.searchAliases || [],
          getRoutePathString: () => getRoutePathString(router, r)
        }))

      const customItems = [
        {
          name: 'custom-my-profile',
          meta: { label: 'My Profile', icon: 'user' },
          isCustom: true,
          getRoutePathString: () => 'User / My Profile',
          action: () => {
            window.open(`${loginServer}/manage`, '_blank')
          }
        },
        {
          name: 'custom-change-password',
          meta: { label: 'Change Password', icon: 'key-round' },
          isCustom: true,
          getRoutePathString: () => 'User / Change Password',
          action: () => {
            window.open(`${loginServer}/change-password`, '_blank')
          }
        },
        {
          name: 'custom-local-settings',
          meta: { label: 'Local Settings', icon: 'settings' },
          isCustom: true,
          getRoutePathString: () => 'User / Local Settings',
          action: () => {
            promptLocalSettings(dialog)
          }
        },
        {
          name: 'custom-switch-organization',
          meta: { label: 'Switch Organization', icon: 'building' },
          isCustom: true,
          getRoutePathString: () => 'User / Switch Organization',
          action: () => {
            router.push('/orgs')
          }
        }
      ]

      return [...routes, ...customItems]
    })

    const filteredRoutes = computed(() =>
      matchSearchableRoutes(searchableRoutes.value, searchQuery.value)
    )

    watch(filteredRoutes, (routes) => {
      if (routes.length === 0) {
        activeSearchIndex.value = -1
      } else if (activeSearchIndex.value >= routes.length) {
        activeSearchIndex.value = 0
      }
    })

    const setActiveSearchIndex = (idx) => {
      activeSearchIndex.value = idx
    }

    const handleSearchEnter = () => {
      if (filteredRoutes.value.length > 0) {
        const selectedIndex = activeSearchIndex.value >= 0
          ? activeSearchIndex.value
          : 0
        navigateSearch(filteredRoutes.value[selectedIndex])
      }
    }

    const handleSearchKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSearchEnter()
        return
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
        return
      }

      const routes = filteredRoutes.value
      if (routes.length === 0) {
        return
      }

      e.preventDefault()

      if (e.key === 'ArrowDown') {
        activeSearchIndex.value = activeSearchIndex.value < routes.length - 1
          ? activeSearchIndex.value + 1
          : 0
      } else {
        activeSearchIndex.value = activeSearchIndex.value > 0
          ? activeSearchIndex.value - 1
          : routes.length - 1
      }
    }

    const navigateSearch = (route) => {
      if (route.isCustom && route.action) {
        route.action()
      } else {
        const orgId = store.state.organizationId
        router.push({ name: route.name, params: { orgId } })
      }
      searchQuery.value = ''
      activeSearchIndex.value = -1
      showMobileSearch.value = false
    }

    const closeSearch = () => {
      searchQuery.value = ''
      activeSearchIndex.value = -1
    }

    const toggleMobileSearch = () => {
      showMobileSearch.value = !showMobileSearch.value
      if (showMobileSearch.value) {
        requestAnimationFrame(() => {
          mobileSearchInput.value?.focus()
        })
      }
    }

    const closeMobileSearch = () => {
      showMobileSearch.value = false
    }

    // Hotkey for search (cmd+K)
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchInput.value?.focus()
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', handleKeyDown)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeyDown)
    })

    // Theme Management
    const theme = ref('balanced')
    const themeOptions = [
      { value: 'system', label: 'System Default', icon: Monitor, iconClass: 'tw:text-slate-400' },
      { value: 'light', label: 'Light Layout', icon: Sun, iconClass: 'tw:text-amber-400' },
      { value: 'balanced', label: 'Balanced Hybrid', icon: Contrast, iconClass: 'tw:text-emerald-400' },
      { value: 'midnight', label: 'Midnight Blue', icon: Moon, iconClass: 'tw:text-blue-400' },
      { value: 'obsidian', label: 'Obsidian Black', icon: SunDim, iconClass: 'tw:text-indigo-400' }
    ]

    const COLOR_SCHEME_STORAGE_KEY = 'color-scheme'
    const mediaQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null
    const systemIsDark = ref(mediaQuery ? mediaQuery.matches : false)

    const normalizeTheme = (value) => {
      if (!value) return 'system'
      if (value === 'dark') return 'obsidian'
      return value
    }

    function applyThemeClasses(actualTheme) {
      const el = document.documentElement
      if (actualTheme === 'light') {
        el.classList.add('light-theme')
        el.classList.remove('app-dark', 'dim-theme', 'balanced-theme')
        el.setAttribute('data-bs-theme', 'light')
      } else if (actualTheme === 'balanced') {
        el.classList.add('light-theme', 'balanced-theme')
        el.classList.remove('app-dark', 'dim-theme')
        el.setAttribute('data-bs-theme', 'light')
      } else if (actualTheme === 'obsidian') {
        el.classList.add('app-dark', 'dim-theme')
        el.classList.remove('light-theme', 'balanced-theme')
        el.setAttribute('data-bs-theme', 'dark')
      } else {
        // midnight
        el.classList.add('app-dark')
        el.classList.remove('light-theme', 'dim-theme', 'balanced-theme')
        el.setAttribute('data-bs-theme', 'dark')
      }
    }

    const resolvedTheme = computed(() => {
      if (theme.value === 'system') {
        return systemIsDark.value ? 'obsidian' : 'light'
      }
      return theme.value
    })

    const isLightOrBalancedTheme = computed(() => resolvedTheme.value === 'light' || resolvedTheme.value === 'balanced')

    if (mediaQuery) {
      mediaQuery.addEventListener('change', (e) => {
        systemIsDark.value = e.matches
        if (theme.value === 'system') {
          applyThemeClasses(e.matches ? 'obsidian' : 'light')
        }
      })
    }

    onMounted(() => {
      const storedScheme = store.state.userPreferences?.colorScheme
      const cachedScheme = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY)
      theme.value = normalizeTheme(storedScheme || cachedScheme || 'balanced')
      applyThemeClasses(resolvedTheme.value)

      // Keep startup cache aligned with server preferences when they exist.
      if (storedScheme) {
        localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, theme.value)
      }
    })

    watch(
      () => store.state.userPreferences?.colorScheme,
      (serverScheme) => {
        if (!serverScheme) return
        const normalized = normalizeTheme(serverScheme)

        if (theme.value !== normalized) {
          theme.value = normalized
          applyThemeClasses(resolvedTheme.value)
        }

        localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, normalized)
      }
    )

    const applyTheme = (newTheme) => {
      theme.value = newTheme
      localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, newTheme)
      applyThemeClasses(resolvedTheme.value)

      store.dispatch('patchUserPreferences', { colorScheme: newTheme }).catch(() => {
        // The localStorage cache above already holds it for this browser.
      })
    }

    // Referral management
    const showReferral = ref(false)
    const closeReferral = () => {
      showReferral.value = false
    }
    const launchLearnMore = () => {
      window.open('https://www.fareclock.com/referral-program', '_blank', 'noopener')
      closeReferral()
    }
    const launchQuestion = () => {
      const email = 'marketing@fareclock.com'
      const subject = encodeURIComponent("Inquiry About Fareclock's Referral Program")
      window.location.href = `mailto:${email}?subject=${subject}`
      closeReferral()
    }

    // Help management
    const showHelp = ref(false)
    const closeHelp = () => {
      showHelp.value = false
    }
    const launchFeedback = () => {
      showModal({ component: FeedbackModal })
      closeHelp()
    }
    const launchWalkthrough = () => {
      promptScheduleWalkthrough(dialog)
      closeHelp()
    }
    const launchTour = async () => {
      closeHelp()

      // Asked for explicitly, so no invitation card -- straight into the tour. The exception is
      // a viewport too narrow to run it, where the invitation doubles as the explanation of why.
      if (window.innerWidth < TOUR_MIN_VIEWPORT_WIDTH) {
        await promptConsoleTourInvitation(dialog, {
          variant: consoleTourStore.variant,
          narrowScreen: true
        })
        return
      }

      await runConsoleTour('help_menu')
    }
    const launchWhatsNew = () => {
      closeHelp()
      whatsNewStore.openPanel('help_menu')
    }
    const launchHelp = () => {
      openIntercom('help')
      closeHelp()
    }
    const launchSupportChat = () => {
      openSupportChat()
      closeHelp()
    }
    const toggleSupportChat = () => {
      toggleIntercomSupportChat()
    }

    // User Profile management
    const showUserMenu = ref(false)
    const closeUserMenu = () => {
      showUserMenu.value = false
    }

    const getLucideIcon = (iconName) => {
      const mapping = {
        'tachometer-alt': LayoutDashboard,
        'clock': Clock,
        'history': Clock,
        'calendar-alt': Calendar,
        'calendar': Calendar,
        'bed': Calendar,
        'plane': Calendar,
        'jet-fighter': Calendar,
        'umbrella-beach': Calendar,
        'island-tropical': Calendar,
        'cog': Settings,
        'gears': Settings,
        'gear': Settings,
        'settings': Settings,
        'user-tie': Users,
        'user': User,
        'users': Users,
        'id-card': User,
        'id-badge': User,
        'user-secret': User,
        'people-roof': Users,
        'columns': Clipboard,
        'table': Clipboard,
        'money-check-dollar': Coins,
        'money-bill-transfer': Coins,
        'money-bill-trend-up': Coins,
        'money-bill': Coins,
        'money-check': Coins,
        'credit-card': CreditCard,
        'chart-line': BarChart3,
        'chart-bar': BarChart3,
        'chart-pie': BarChart3,
        'analytics': BarChart3,
        'bullhorn': Cpu,
        'magic': Wand2,
        'sitemap': Network,
        'briefcase': Briefcase,
        'badge-check': Shield,
        'balance-scale': Shield,
        'sign-in-alt': LogOut,
        'lightbulb': HelpCircle,
        'triangle-exclamation': AlertTriangle,
        'home': Home,
        'bell': Bell,
        'file-alt': FileText,
        'file-text': FileText,
        'key-round': KeyRound,
        'building': Building,
        'crown': Crown
      }
      return mapping[iconName] || FileText
    }

    // Pages carry a Lucide `navIcon` chosen for the sidebar; older meta only has a FontAwesome name.
    const getRouteIcon = (meta) => isNavIconName(meta?.navIcon) ? resolveNavIcon(meta.navIcon) : getLucideIcon(meta?.icon)

    return {
      activePageDisplayName,
      searchQuery,
      searchInput,
      mobileSearchInput,
      showMobileSearch,
      searchShortcutModifier,
      filteredRoutes,
      activeSearchIndex,
      setActiveSearchIndex,
      handleSearchEnter,
      handleSearchKeyDown,
      navigateSearch,
      closeSearch,
      toggleMobileSearch,
      closeMobileSearch,
      theme,
      themeOptions,
      applyTheme,
      isLightOrBalancedTheme,
      showReferral,
      closeReferral,
      launchLearnMore,
      launchQuestion,
      showHelp,
      closeHelp,
      launchFeedback,
      launchWalkthrough,
      launchTour,
      launchWhatsNew,
      whatsNewUnreadCount,
      supportChatAvailable,
      supportUnreadCount,
      launchSupportChat,
      toggleSupportChat,
      tourMenuLabel: TOUR_MENU_LABEL,
      launchHelp,
      legacyConsoleRevertUrl,
      isRevertingToLegacy,
      onRevertToLegacyClick,
      showUserMenu,
      closeUserMenu,
      dialog,
      loginServer,
      getRouteIcon
    }
  },
  computed: {
    ...mapState({
      dashboardNotificationsEnabled: state => state.dashboardNotifications.running,
      organizationName: state => state.organizationName,
      organizationId: state => state.organizationId
    }),
    ...mapState('userProfile', ['countryCode', 'userName', 'emailAddress']),
    ...mapGetters('userProfile', ['isSudoAccessToAllOrgs']),
    ...mapState('jwt', ['idaasClaims']),
    breadcrumbItems () {
      const normalizeSectionLabel = label => String(label || '').toLowerCase().trim().replace(/\s+center$/, '')

      const sidebarRouteNames = {
        overview: 'dashboard',
        attendance: 'attendance-index',
        schedule: 'schedule-index',
        devices: 'device-list',
        users: 'employee-list',
        timeoff: 'timeoff-index',
        forms: 'forms-list',
        costing: 'job-list',
        payroll: 'payroll-index',
        systemops: 'system-ops',
        reports: 'reports',
        byxbi: 'byxbi',
        system: 'settings'
      }

      const getDefaultChildRouteName = (routeRecord, allRoutes, seen = new Set()) => {
        if (!routeRecord?.name || seen.has(routeRecord.name)) {
          return null
        }

        seen.add(routeRecord.name)

        if (!routeRecord.children || routeRecord.children.length === 0) {
          return null
        }

        const defaultChild = routeRecord.children.find(child => child.path === '' || child.path === '/')
        if (!defaultChild) {
          return null
        }

        if (defaultChild.redirect && typeof defaultChild.redirect !== 'function') {
          if (defaultChild.redirect.name) {
            const redirected = allRoutes.find(r => r.name === defaultChild.redirect.name)
            const nestedTarget = getDefaultChildRouteName(redirected, allRoutes, seen)
            return nestedTarget || defaultChild.redirect.name
          }

          if (defaultChild.redirect.path) {
            const resolved = this.$router.resolve({ path: defaultChild.redirect.path })
            if (resolved?.name) {
              const redirected = allRoutes.find(r => r.name === resolved.name)
              const nestedTarget = getDefaultChildRouteName(redirected, allRoutes, seen)
              return nestedTarget || resolved.name
            }
          }
        }

        if (defaultChild.name) {
          const defaultRecord = allRoutes.find(r => r.name === defaultChild.name)
          const nestedTarget = getDefaultChildRouteName(defaultRecord, allRoutes, seen)
          return nestedTarget || defaultChild.name
        }

        return null
      }

      const getRouteLocation = (routeRecord) => {
        if (!routeRecord?.name) {
          return null
        }

        const allRoutes = this.$router.getRoutes()
        const canonicalRouteRecord = allRoutes.find(r => r.name === routeRecord.name) || routeRecord
        const defaultRouteName = getDefaultChildRouteName(canonicalRouteRecord, allRoutes)
        const targetRouteName = defaultRouteName || routeRecord.name
        const targetRouteRecord = allRoutes.find(r => r.name === targetRouteName)

        const paramNames = new Set()
        for (const routePath of [canonicalRouteRecord.path, targetRouteRecord?.path]) {
          for (const match of routePath?.matchAll(/:([A-Za-z0-9_]+)/g) || []) {
            paramNames.add(match[1])
          }
        }

        const params = {}
        for (const key of paramNames) {
          if (this.$route.params[key] !== undefined) {
            params[key] = this.$route.params[key]
          }
        }

        const resolved = this.$router.resolve({ name: targetRouteName, params })
        return resolved?.name ? { name: targetRouteName, params } : null
      }

      const getSidebarRouteLocation = (activePage) => {
        const routeName = sidebarRouteNames[activePage]
        if (!routeName) {
          return null
        }

        const routeRecord = this.$router.getRoutes().find(r => r.name === routeName)
        return getRouteLocation(routeRecord)
      }

      const items = []
      
      // Level 1: Organization Name or "Platform"
      if (this.organizationId && this.organizationName) {
        items.push({
          label: this.organizationName,
          to: { path: `/orgs/${this.organizationId}` }
        })
      } else {
        items.push({
          label: 'Platform',
          to: { name: 'org-selector' }
        })
      }

      const matched = this.$route.matched
      if (!matched || matched.length === 0) {
        return items
      }

      // Find the index of 'org-namespace' in matched routes
      const orgNamespaceIdx = matched.findIndex(m => m.name === 'org-namespace')

      if (orgNamespaceIdx !== -1) {
        // We are inside an organization namespace.
        // Level 2 should align with the sidebar button chosen.
        const sidebarLabel = sidebarLabels[this.activePage]
        const sectionRouteRecord = matched[orgNamespaceIdx + 1]
        if (sidebarLabel) {
          items.push({
            label: sidebarLabel,
            to: getSidebarRouteLocation(this.activePage) || getRouteLocation(sectionRouteRecord)
          })
        }

        // Level 3 and 4 should be views underneath the sidebar button.
        const subRoutes = matched.slice(orgNamespaceIdx + 1)
        for (const routeRecord of subRoutes) {
          const label = routeRecord.meta?.label || routeRecord.meta?.title || routeRecord.name
          if (!label) continue
          
          const normLabel = label.toLowerCase().trim()
          const normalizedRouteSectionLabel = normalizeSectionLabel(normLabel)
          const normalizedSidebarSectionLabel = normalizeSectionLabel(sidebarLabel)
          
          // Skip if it duplicates the sidebar label (e.g. 'settings' matches 'Settings')
          if (sidebarLabel && normalizedRouteSectionLabel === normalizedSidebarSectionLabel) {
            continue
          }
          
          // Also skip generic grouping route names or 'dashboard' / 'overview'
          if (normLabel === 'settings' || normLabel === 'dashboard' || normLabel === 'overview') {
            continue
          }
          
          items.push({
            label,
            to: getRouteLocation(routeRecord)
          })
        }
      } else {
        // We are outside an organization namespace (e.g., /orgs or /sudo).
        const subRoutes = matched.slice(1)
        for (const routeRecord of subRoutes) {
          const label = routeRecord.meta?.label || routeRecord.meta?.title || routeRecord.name
          if (label) {
            items.push({
              label,
              to: getRouteLocation(routeRecord)
            })
          }
        }
      }

      // De-duplicate adjacent identical breadcrumbs
      const uniqueItems = []
      for (const item of items) {
        if (uniqueItems.length === 0 || uniqueItems[uniqueItems.length - 1].label !== item.label) {
          uniqueItems.push(item)
        }
      }

      return uniqueItems
    },
    showReferralDropdown () {
      return this.countryCode !== 'PH'
    },
    avatarUrl () {
      return this.idaasClaims?.picture
    },
    avatarText () {
      return this.avatarUrl ? null : this.idaasClaims?.name?.match(/(\b\S)?/g)?.join('')?.match(/(^\S|\S$)?/g)?.join('')?.toUpperCase()
    }
  },
  methods: {
    ...mapActions(['logout']),
    goToSudo () {
      this.$router.push('/sudo')
      this.closeUserMenu()
    },
    onLocalSettingsClick () {
      promptLocalSettings(this.dialog)
      this.closeUserMenu()
    },
    onSwitchOrgClick () {
      this.$router.push('/orgs')
      this.closeUserMenu()
    },
    onLogoutClick () {
      this.logout()
      this.closeUserMenu()
    }
  }
}
</script>

<style scoped>
/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s cubic-bezier(0, 0, 0.2, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

/* Reset button focus outlines/shadows from Bootstrap clashing */
button {
  border: 0 solid transparent;
}
button:focus,
button:active {
  outline: none !important;
  box-shadow: none !important;
}

.breadcrumb-track {
  flex-wrap: nowrap !important;
  white-space: nowrap !important;
}

.breadcrumb-separator {
  flex: 0 0 auto;
}

.breadcrumb-segment {
  display: inline-block;
  min-width: 0;
  max-width: 10rem;
  height: 1.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap !important;
  overflow-wrap: normal !important;
  word-break: normal !important;
  line-height: 1.25rem;
  vertical-align: middle;
}

.breadcrumb-segment-current {
  max-width: 18rem;
}

.mobile-breadcrumb-track {
  overflow: hidden;
}

.mobile-breadcrumb-segment {
  display: inline-block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.25rem;
  vertical-align: middle;
}

.mobile-breadcrumb-org {
  flex: 0 1 auto;
  max-width: 62%;
}

.mobile-breadcrumb-tail {
  flex: 0 1 auto;
  max-width: 8rem;
}

.header-search-full {
  display: none;
}

/* Use full search bar in the mid-width range where mobile sidebar is gone but md breakpoint is not reached yet. */
@media (min-width: 520px) {
  .header-search-full {
    display: block;
  }

  .mobile-search-toggle {
    display: none;
  }
}

@media (min-width: 768px) and (max-width: 1279px) {
  .header-actions {
    margin-left: 0;
    width: 100%;
  }
}

/* Preserve previous md+ behavior explicitly. */
@media (min-width: 768px) {
  .header-search-full {
    display: block;
  }

  .mobile-search-toggle {
    display: none;
  }
}
</style>
